import { Move, UserProgress } from '../types';

const STORAGE_KEY = 'emma_gymnastics_progress_v1';

export const DEFAULT_PROGRESS: UserProgress = {
  completedStepIds: {},
  earnedStars: 0,
  activeFriendId: 'friend-backbend',
  masteredMoveIds: {},
  lastUpdated: new Date().toISOString(),
};

export function loadProgress(): UserProgress {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_PROGRESS;
    const parsed = JSON.parse(raw);
    return {
      ...DEFAULT_PROGRESS,
      ...parsed,
    };
  } catch (err) {
    console.warn('Failed to load gymnastics progress from localStorage, using defaults', err);
    return DEFAULT_PROGRESS;
  }
}

export function saveProgress(progress: UserProgress): void {
  try {
    const updated = {
      ...progress,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (err) {
    console.warn('Failed to save gymnastics progress to localStorage', err);
  }
}

export function resetProgress(): UserProgress {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    console.warn('Failed to clear progress', err);
  }
  return DEFAULT_PROGRESS;
}

export function toggleStepCompletion(
  current: UserProgress,
  stepId: string,
  move: Move
): { updatedProgress: UserProgress; nowCompleted: boolean; moveJustMastered: boolean } {
  const isAlreadyCompleted = !!current.completedStepIds[stepId];
  const nowCompleted = !isAlreadyCompleted;

  const newCompleted = { ...current.completedStepIds };
  if (nowCompleted) {
    newCompleted[stepId] = true;
  } else {
    delete newCompleted[stepId];
  }

  // Check if all steps in this move are completed
  const allStepsComplete = move.steps.every(s => newCompleted[s.id]);
  const wasMoveMastered = !!current.masteredMoveIds[move.id];
  const moveJustMastered = allStepsComplete && !wasMoveMastered;

  const newMastered = { ...current.masteredMoveIds };
  if (allStepsComplete) {
    newMastered[move.id] = true;
  } else {
    delete newMastered[move.id];
  }

  // Calculate stars: 1 star per completed step + 5 bonus stars per mastered move
  const totalCompletedSteps = Object.keys(newCompleted).length;
  const totalMasteredMoves = Object.keys(newMastered).length;
  const earnedStars = totalCompletedSteps + totalMasteredMoves * 5;

  const updatedProgress: UserProgress = {
    ...current,
    completedStepIds: newCompleted,
    masteredMoveIds: newMastered,
    earnedStars,
    lastUpdated: new Date().toISOString(),
  };

  saveProgress(updatedProgress);

  return {
    updatedProgress,
    nowCompleted,
    moveJustMastered,
  };
}
